import { cn } from '@/lib/utils'
import React from 'react'

export type IndicatorStyle = { left: number; width: number }

export const HIDDEN_INDICATOR: IndicatorStyle = { left: 0, width: 0 }

/**
 * Parks the sliding indicator on the active tab, measured in a layout effect so the underline never
 * trails the tab it belongs to by a frame.
 *
 * The refs are not always populated on the first pass: a tab list that just changed shape, or counts
 * that widen a tab once they load. So a failed measurement retries on the next frame.
 * `hideWhenUnmeasurable` is for the case where there is no tab to underline at all, or the active one
 * lives out of sight in a dropdown, where the indicator should disappear rather than stay painted
 * over a tab it no longer belongs to.
 */
export function useSlidingIndicator(
	activeIndex: number,
	tabRefs: React.RefObject<(HTMLElement | null)[]>,
	containerRef: React.RefObject<HTMLElement | null>,
	{ hideWhenUnmeasurable = false }: { hideWhenUnmeasurable?: boolean },
	triggers: React.DependencyList,
): IndicatorStyle {
	const [style, setStyle] = React.useState<IndicatorStyle>(HIDDEN_INDICATOR)
	const activeIndexRef = React.useRef(activeIndex)
	const remeasureRef = React.useRef<() => void>(() => {})
	const observedRef = React.useRef<{ container: HTMLElement | null; tab: HTMLElement | null }>({
		container: null,
		tab: null,
	})
	const observerRef = React.useRef<ResizeObserver | null>(null)

	// biome-ignore lint/correctness/useExhaustiveDependencies: callers pass the values that change tab geometry
	React.useLayoutEffect(() => {
		activeIndexRef.current = activeIndex

		const measure = (index: number): IndicatorStyle | null => {
			const tab = tabRefs.current?.[index]
			const container = containerRef.current
			if (!tab || !container || index < 0) {
				return null
			}
			const containerRect = container.getBoundingClientRect()
			const tabRect = tab.getBoundingClientRect()
			return { left: tabRect.left - containerRect.left, width: tabRect.width }
		}

		const applyMeasurement = (): boolean => {
			const measured = measure(activeIndexRef.current)
			if (measured) {
				// Bail out on unchanged geometry: the observer fires for every frame of a resize
				// animation, and a fresh object would re-render the whole header on each one.
				setStyle(current => (current.left === measured.left && current.width === measured.width ? current : measured))
				return true
			}
			if (hideWhenUnmeasurable) {
				setStyle(current => (current.width === 0 ? current : HIDDEN_INDICATOR))
			}
			return false
		}
		remeasureRef.current = applyMeasurement

		if (applyMeasurement()) {
			return
		}

		// Retrying reads the ref rather than a captured index, so a re-run that lands before the frame
		// cancels it and a run that does not still measures the tab that is active by then.
		const rafId = requestAnimationFrame(applyMeasurement)
		return () => cancelAnimationFrame(rafId)
	}, triggers)

	// The strip also moves when nothing in `triggers` changes: collapsing the sidebar or resizing the
	// window re-lays out the row, and the underline would keep the geometry it measured before. The
	// active tab is watched too: its count badge counts up after the measurement, and the full width
	// container does not change size when the tab widens.
	//
	// Deliberately runs after every commit rather than on a dependency list. A RefObject's identity
	// never changes, so keying on it would run this once, at a commit where the container may not be
	// mounted yet, and never again. Callers mount theirs late (a placeholder renders first) and swap
	// between two container elements, so the element itself is the thing to watch. The identity check
	// below makes the repeat runs cost one comparison.
	React.useEffect(() => {
		const container = containerRef.current
		const tab = activeIndex >= 0 ? (tabRefs.current?.[activeIndex] ?? null) : null
		const observed = observedRef.current
		if (container === observed.container && tab === observed.tab) {
			return
		}
		observerRef.current?.disconnect()
		observedRef.current = { container, tab }
		if (!container) {
			observerRef.current = null
			return
		}
		const observer = new ResizeObserver(() => remeasureRef.current())
		observer.observe(container)
		if (tab) {
			observer.observe(tab)
		}
		observerRef.current = observer
	})

	React.useEffect(() => {
		return () => {
			observerRef.current?.disconnect()
			observerRef.current = null
			observedRef.current = { container: null, tab: null }
		}
	}, [])

	return style
}

export function SlidingIndicator({
	style,
	className,
	dataTestId,
}: { style: IndicatorStyle; className?: string; dataTestId?: string }) {
	return (
		<div
			data-testid={dataTestId}
			className={cn(
				'absolute top-0 h-control bg-header-tab-active-bg rounded-[6px] transition-all duration-300 ease-in-out pointer-events-none z-0',
				className,
			)}
			style={{ left: `${style.left}px`, width: `${style.width}px`, opacity: style.width === 0 ? 0 : 1 }}
		/>
	)
}
